// The authoritative description of the periodic jobs: what each one is called,
// the route that performs it, when it runs, and the two thresholds derived
// from its interval. This lived in four hand-maintained copies before —
// apps/worker's job list, vercel.json, EXPECTED_WORKER_JOBS and
// JOB_STALENESS_MS — and they had already drifted: sync-search-index ran on a
// schedule and recorded a heartbeat that readiness never looked at.

export interface WorkerJob {
  name: string;
  /** The bearer-gated route that does the work and records the heartbeat. */
  path: string;
  /** Standard five-field cron expression, node-cron's own syntax. */
  schedule: string;
  /** The nominal gap between runs, matching `schedule`. */
  intervalMs: number;
  /**
   * How overdue a heartbeat must be before `/readyz` reports the job stale.
   * Generous — four intervals for the frequent jobs, a six-hour grace on the
   * daily ones — so scheduling jitter never pages on its own.
   */
  stalenessMs: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

export const WORKER_JOBS = [
  {
    name: 'drain-analytics-outbox',
    path: '/api/internal/cron/drain-analytics-outbox',
    schedule: '*/5 * * * *',
    intervalMs: 5 * MINUTE,
    stalenessMs: 20 * MINUTE,
  },
  {
    name: 'sweep-runs',
    path: '/api/internal/cron/sweep-runs',
    schedule: '*/5 * * * *',
    intervalMs: 5 * MINUTE,
    stalenessMs: 20 * MINUTE,
  },
  {
    name: 'compute-analytics-rollups',
    path: '/api/internal/cron/compute-analytics-rollups',
    schedule: '5 * * * *',
    intervalMs: HOUR,
    stalenessMs: 4 * HOUR,
  },
  {
    name: 'purge-analytics',
    path: '/api/internal/cron/purge-analytics',
    schedule: '0 3 * * *',
    intervalMs: 24 * HOUR,
    stalenessMs: 30 * HOUR,
  },
  {
    name: 'check-reward-ledger',
    path: '/api/internal/cron/check-reward-ledger',
    schedule: '0 4 * * *',
    intervalMs: 24 * HOUR,
    stalenessMs: 30 * HOUR,
  },
  {
    name: 'check-analytics-reconciliation',
    path: '/api/internal/cron/check-analytics-reconciliation',
    schedule: '15 4 * * *',
    intervalMs: 24 * HOUR,
    stalenessMs: 30 * HOUR,
  },
  {
    name: 'check-fraud-signals',
    path: '/api/internal/cron/check-fraud-signals',
    schedule: '*/15 * * * *',
    intervalMs: 15 * MINUTE,
    stalenessMs: HOUR,
  },
  {
    name: 'check-moderation-queue',
    path: '/api/internal/cron/check-moderation-queue',
    schedule: '0 * * * *',
    intervalMs: HOUR,
    stalenessMs: 4 * HOUR,
  },
  {
    name: 'check-redis-memory',
    path: '/api/internal/cron/check-redis-memory',
    schedule: '*/15 * * * *',
    intervalMs: 15 * MINUTE,
    stalenessMs: HOUR,
  },
  {
    name: 'sync-search-index',
    path: '/api/internal/cron/sync-search-index',
    schedule: '20 * * * *',
    intervalMs: HOUR,
    stalenessMs: 4 * HOUR,
  },
] as const satisfies readonly WorkerJob[];

export type WorkerJobName = (typeof WORKER_JOBS)[number]['name'];

export const WORKER_JOB_NAMES = WORKER_JOBS.map((job) => job.name) as readonly WorkerJobName[];

/**
 * How long past its interval a job must be before the worker treats the slot
 * as missed and runs it on sight. Always smaller than `stalenessMs`: catch-up
 * exists to recover the run before readiness would page for it, and the
 * ordering is asserted in the tests rather than left to the table's authors.
 *
 * The grace is a quarter of the interval, capped at an hour, so a five-minute
 * job is not re-run over ordinary jitter and a daily one does not wait an
 * extra day to notice.
 */
export function catchUpAfterMs(job: WorkerJob): number {
  return job.intervalMs + Math.min(job.intervalMs * 0.25, HOUR);
}

/**
 * A job with no heartbeat at all is not overdue. That is indistinguishable
 * from a deployment whose first tick has not arrived yet — the same reasoning
 * `/readyz` applies for `neverRun` — and treating it as missed would fire
 * every job at once against an empty database.
 */
export function isJobOverdue(job: WorkerJob, lastRunAt: Date | null | undefined, now: number = Date.now()): boolean {
  if (!lastRunAt) return false;
  return now - lastRunAt.getTime() > catchUpAfterMs(job);
}
